/**
 * Group Operations Module
 *
 * This module contains utilities for group-related operations including:
 * - Layout group with alignment (center, left, right)
 * - Layout group children horizontally
 * - Position groups by alignment
 * - Get group reference positions
 * - Find parent group with alignment
 * - Redistribute groups by grid (rows x columns)
 * - Calculate grid positions for groups
 * - Find groups by path
 * - Get group count
 */

import { FABRIC_CUSTOM_PROPS } from "constants/ThumbnailEditor";

import { findObjectByPath } from "./form-structure";

/**
 * Find the first parent group (traversing up the path) that has an alignment property
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {string} path - Starting object path
 * @returns {{ group: Object, path: string, alignment: string }|null} Parent group info or null
 */
export const findParentGroupWithAlignment = (canvas, path) => {
  if (!canvas || !path) return null;

  // Parse path to get parent group path
  const pathParts = path.split(".");

  // Check each level from current to root
  for (let i = pathParts.length - 1; i >= 0; i--) {
    const currentPath = pathParts.slice(0, i).join(".");
    if (!currentPath || currentPath === "objects") continue;

    const obj = findObjectByPath(canvas, currentPath);
    const alignment = obj?.templatePayload?.alignment;

    if (obj && obj.type === "group" && alignment) {
      return { group: obj, path: currentPath, alignment };
    }
  }

  return null;
};

/**
 * Layout children of a group in a single line with spacing
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {Object} group - Group object to layout
 */
const layoutGroupChildren = (canvas, group) => {
  // In Fabric.js, group children are in _objects, not objects
  const children = group._objects || group.objects;

  if (!group || group.type !== "group" || !children || children.length === 0) {
    console.log("❌ [layoutGroupChildren] Invalid group or no children");
    return;
  }

  // Sort children by their current left position to maintain order
  const sortedChildren = [...children].sort((a, b) => a.left - b.left);

  // Use fixed spacing instead of calculating from old positions
  // When text changes, old positions create incorrect spacing calculations
  const groupSpacing =
    parseInt(group.templatePayload?.horizontalSpacing) || parseFloat(group.templatePayload?.spacing) || 10;
  const spacing = groupSpacing === 0 ? 1 : groupSpacing || 10; // Fixed 10px spacing between items

  // Calculate the actual width each child occupies after updates
  const childWidths = sortedChildren.map((child) => child.getScaledWidth());

  // Calculate total width: sum of all widths + spacing between them
  const totalWidth = childWidths.reduce((sum, width) => sum + width, 0) + spacing * (children.length - 1);

  // Start from the left edge, positioning relative to group center (0,0)
  // Group center is at (0,0), so we start at -totalWidth/2 to center everything
  let currentLeft = -totalWidth / 2;

  // Reposition each child
  sortedChildren.forEach((child, index) => {
    const childWidth = childWidths[index];

    // Position child's left edge at currentLeft
    // In Fabric groups, position is from the object's origin point
    child.set({
      left: currentLeft,
      top: 0,
      originX: "left", // Origin at left edge for easier calculation
      originY: "center", // Center vertically
    });

    // Move currentLeft to the right by this child's width + spacing
    currentLeft += childWidth + spacing;
  });

  // Force Fabric.js to recalculate the group's width and height
  // This is CRITICAL - without this, the group won't resize to fit new content
  group.addWithUpdate();
  group.setCoords();
};

/**
 * Position a group based on its alignment setting, maintaining its original reference point
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {Object} group - Group object to position
 * @param {string} groupPath - Path to the group
 * @param {string} alignment - Alignment type: "center", "left", or "right"
 * @param {number} originalReference - Original reference position to maintain
 */
const positionGroupByAlignment = (canvas, group, groupPath, alignment, originalReference) => {
  const pathParts = groupPath.split(".");
  const isNested = pathParts.length > 1;

  if (isNested) {
    // Nested group - position within parent group
    const parentPath = pathParts.slice(0, -1).join(".");
    const parentGroup = findObjectByPath(canvas, parentPath);

    if (parentGroup && parentGroup.type === "group") {
      // Store current vertical position
      const currentTop = group.top;
      const currentOriginY = group.originY || "center";

      // Adjust top if origin changes
      let adjustedTop = currentTop;
      if (currentOriginY !== "center") {
        const groupHeight = group.getScaledHeight();
        if (currentOriginY === "top") {
          adjustedTop = currentTop + groupHeight / 2;
        } else if (currentOriginY === "bottom") {
          adjustedTop = currentTop - groupHeight / 2;
        }
      }

      // Set origin and position based on alignment
      let newLeft;
      if (alignment === "center") {
        // Center: originX center, maintain center position
        group.set({ originX: "center" });
        newLeft = originalReference; // Maintain original center position
      } else if (alignment === "left") {
        // Left: originX left, maintain left edge position
        group.set({ originX: "left" });
        newLeft = originalReference;
      } else if (alignment === "right") {
        // Right: originX right, maintain right edge position
        group.set({ originX: "right" });
        newLeft = originalReference;
      }

      group.set({
        originY: "center",
        left: newLeft,
        top: adjustedTop,
      });
    }
  } else {
    // Top-level group - maintain original position
    // Set origin based on alignment
    if (alignment === "center") {
      group.set({ originX: "center" });
    } else if (alignment === "left") {
      group.set({ originX: "left" });
    } else if (alignment === "right") {
      group.set({ originX: "right" });
    }

    // Use originalReference for all alignments - it contains the position to maintain
    group.set({ left: originalReference });
  }

  group.setCoords();
};

/**
 * Get the reference position for a group based on its alignment
 * This is the position we want to maintain when the group's width changes
 *
 * @param {Object} group - Group object
 * @param {string} alignment - Alignment type: "center", "left", or "right"
 * @returns {number} Reference position value
 */
export const getGroupReferencePosition = (group, alignment) => {
  if (group?._alignmentReference) {
    return group?._alignmentReference;
  }

  const groupWidth = group.getScaledWidth();
  const currentLeft = group.left;
  const currentOriginX = group.originX || "center";

  // Calculate the actual position of left/center/right edges
  let leftEdge, centerPos, rightEdge;

  if (currentOriginX === "center") {
    centerPos = currentLeft;
    leftEdge = currentLeft - groupWidth / 2;
    rightEdge = currentLeft + groupWidth / 2;
  } else if (currentOriginX === "left") {
    leftEdge = currentLeft;
    centerPos = currentLeft + groupWidth / 2;
    rightEdge = currentLeft + groupWidth;
  } else if (currentOriginX === "right") {
    rightEdge = currentLeft;
    centerPos = currentLeft - groupWidth / 2;
    leftEdge = currentLeft - groupWidth;
  }

  // Return the position to maintain based on desired alignment
  let reference;
  if (alignment === "center") {
    reference = centerPos;
  } else if (alignment === "left") {
    reference = leftEdge;
  } else if (alignment === "right") {
    reference = rightEdge;
  }

  return reference;
};

/**
 * Layout and position a group based on its alignment property
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {string} groupPath - Path to the group object
 */
export const layoutGroupWithAlignment = (canvas, groupPath) => {
  if (!canvas || !groupPath) {
    return;
  }

  const group = findObjectByPath(canvas, groupPath);
  const alignment = group?.templatePayload?.alignment;

  if (!group || group.type !== "group") {
    console.log("❌ [layoutGroupWithAlignment] Not a group");
    return;
  }

  if (!alignment) {
    console.log("❌ [layoutGroupWithAlignment] No alignment defined");
    return;
  }

  // Step 1: Store the original reference position based on alignment
  const originalReference = getGroupReferencePosition(group, alignment);

  // Step 2: Layout children horizontally (this will change the group's width)
  layoutGroupChildren(canvas, group);

  // Step 3: Reposition the group to maintain its reference point
  positionGroupByAlignment(canvas, group, groupPath, alignment, originalReference);

  // Update group coordinates
  group.setCoords();

  // Step 4: Update parent groups if nested
  const pathParts = groupPath.split(".");

  // Traverse up and update all parent groups
  for (let i = pathParts.length - 1; i > 0; i--) {
    const parentPath = pathParts.slice(0, i).join(".");
    if (!parentPath || parentPath === "objects") continue;

    const parentGroup = findObjectByPath(canvas, parentPath);
    if (parentGroup && parentGroup.type === "group") {
      // Store the parent's current position before update
      const parentLeft = parentGroup.left;
      const parentTop = parentGroup.top;

      // Update bounds (this might move the group)
      parentGroup.addWithUpdate();

      // Restore the parent's original position (don't let it move)
      parentGroup.set({
        left: parentLeft,
        top: parentTop,
      });

      parentGroup.setCoords();
    }
  }

  canvas.requestRenderAll();
};

/**
 * Find all groups at a specific path (by matching the base path pattern)
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {string} basePath - Base path to match (e.g., "objects[0]")
 * @param {Object} templatePayload - Optional template payload to match
 * @returns {Array} Array of group objects with their paths and indices
 */
export const findGroupsByPath = (canvas, basePath, templatePayload = null) => {
  if (!canvas || !basePath) return [];

  const objects = canvas.getObjects();
  const groups = [];

  // Extract the index from basePath (e.g., "objects[0]" -> 0)
  const match = basePath.match(/objects\[(\d+)\]/);
  if (!match) return [];

  const baseIndex = parseInt(match[1], 10);
  const baseObject = objects[baseIndex];

  if (!baseObject || baseObject.type !== "group") return [];

  // Find all groups with the same templatePayload key
  const targetKey = templatePayload?.key || baseObject.templatePayload?.key;

  objects.forEach((obj, index) => {
    if (obj.type === "group" && obj.templatePayload?.key === targetKey) {
      groups.push({
        object: obj,
        path: `objects[${index}]`,
        index,
      });
    }
  });

  return groups;
};

/**
 * Calculate grid positions for groups based on maxRows, maxCols, and spacing
 * Row-first filling: fills first row completely before moving to next row
 *
 * @param {number} count - Number of items to position
 * @param {number} maxRows - Maximum rows in the grid
 * @param {number} maxCols - Maximum columns in the grid
 * @param {number} itemWidth - Width of a single item
 * @param {number} itemHeight - Height of a single item
 * @param {number} horizontalSpacing - Spacing between items
 * @param {number} verticalSpacing - Spacing between items
 * @param {Object} center - Center point {centerX, centerY}
 * @returns {Array} Array of positions {top, left}
 */
export const calculateGridPositions = (
  count,
  maxRows,
  maxCols,
  itemWidth,
  itemHeight,
  horizontalSpacing,
  verticalSpacing,
  center,
) => {
  const positions = [];

  // Calculate positions for each item (row-first)
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / maxCols);
    const col = i % maxCols;

    // Calculate how many items are in this row
    const itemsInRow = Math.min(maxCols, count - row * maxCols);

    // Calculate the total width and height of the current row
    const rowWidth = itemsInRow * itemWidth + (itemsInRow - 1) * horizontalSpacing;

    // Calculate position relative to center
    // For horizontal: center the row
    const xOffset = col * (itemWidth + horizontalSpacing) - rowWidth / 2 + itemWidth / 2;

    // For vertical: calculate total grid height and center it
    const totalRows = Math.ceil(count / maxCols);
    const totalHeight = totalRows * itemHeight + (totalRows - 1) * verticalSpacing;
    const yOffset = row * (itemHeight + verticalSpacing) - totalHeight / 2 + itemHeight / 2;

    positions.push({
      left: center.centerX + xOffset - itemWidth / 2,
      top: center.centerY + yOffset - itemHeight / 2,
    });
  }

  return positions;
};

/**
 * Redistribute groups on canvas based on grid layout (maxRows x maxCols)
 * All groups will be positioned in a grid centered around the original center point
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {string} basePath - Base path of the group to redistribute
 * @param {number} targetCount - Target number of groups
 * @param {number} maxRows - Maximum rows in the grid
 * @param {number} maxCols - Maximum columns in the grid
 * @param {number} horizontalSpacing - Spacing between groups in pixels
 * @param {number} verticalSpacing - Spacing between groups in pixels
 * @returns {Promise<void>}
 */
export const redistributeGroupsByGrid = async (
  canvas,
  basePath,
  targetCount,
  maxRows,
  maxCols,
  horizontalSpacing,
  verticalSpacing,
) => {
  if (!canvas || !basePath) return;

  // Ensure all numeric values are numbers, not strings
  maxRows = parseInt(maxRows, 10) || 1;
  maxCols = parseInt(maxCols, 10) || 1;
  horizontalSpacing = parseFloat(horizontalSpacing) || 10;
  verticalSpacing = parseFloat(verticalSpacing) || 10;
  targetCount = parseInt(targetCount, 10);

  // Find all groups at this path
  const groups = findGroupsByPath(canvas, basePath);
  const currentCount = groups.length;

  if (currentCount === 0) {
    console.warn("⚠️ [redistributeGroupsByGrid] No groups found at path:", basePath);
    return;
  }

  if (currentCount === targetCount) {
    console.log("✅ [redistributeGroupsByGrid] Already at target count");
    return;
  }

  // Validate maxCount
  const maxCount = maxRows * maxCols;
  if (targetCount > maxCount) {
    console.warn(
      `⚠️ [redistributeGroupsByGrid] Target count ${targetCount} exceeds max ${maxCount} (${maxRows}x${maxCols})`,
    );
    return;
  }

  // Find the reference group - use the one that has _originalCenterX stored
  // This ensures we always use the same reference point even after canvas indices change
  let referenceGroup = groups.find((g) => g.object._originalCenterX !== undefined);
  if (!referenceGroup) {
    // First time - use the first group as reference
    referenceGroup = groups[0];
  }

  const refObj = referenceGroup.object;

  // Use stored original center if available, otherwise calculate and store it
  // This prevents center drift when count changes multiple times
  let centerPoint;
  if (refObj._originalCenterX !== undefined && refObj._originalCenterY !== undefined) {
    // Use the stored original center
    centerPoint = {
      centerX: refObj._originalCenterX,
      centerY: refObj._originalCenterY,
    };
  } else {
    // First time: calculate center from current position and store it ON ALL GROUPS
    centerPoint = {
      centerX: refObj.left + (refObj.width * refObj.scaleX) / 2,
      centerY: refObj.top + (refObj.height * refObj.scaleY) / 2,
    };
    // Store the original center on ALL existing groups to mark them all with the same reference
    groups.forEach((g) => {
      g.object._originalCenterX = centerPoint.centerX;
      g.object._originalCenterY = centerPoint.centerY;
    });
  }

  // Use the reference group for cloning and dimensions
  const itemWidth = refObj.width * refObj.scaleX;
  const itemHeight = refObj.height * refObj.scaleY;

  // Calculate grid positions
  const positions = calculateGridPositions(
    targetCount,
    maxRows,
    maxCols,
    itemWidth,
    itemHeight,
    horizontalSpacing,
    verticalSpacing,
    centerPoint,
  );

  // Handle adding new groups
  if (currentCount < targetCount) {
    const groupsToAdd = targetCount - currentCount;

    // Clone groups asynchronously
    const clonePromises = [];
    for (let i = 0; i < groupsToAdd; i++) {
      const promise = new Promise((resolve) => {
        refObj.clone((clonedGroup) => {
          const positionIndex = currentCount + i;
          // Preserve the original center on cloned groups
          clonedGroup.set({
            top: positions[positionIndex].top,
            left: positions[positionIndex].left,
            _originalCenterX: centerPoint.centerX,
            _originalCenterY: centerPoint.centerY,
          });
          canvas.add(clonedGroup);
          clonedGroup.setCoords();
          resolve(clonedGroup);
        }, FABRIC_CUSTOM_PROPS);
      });
      clonePromises.push(promise);
    }

    await Promise.all(clonePromises);
  }

  // Handle removing excess groups
  if (currentCount > targetCount) {
    // Remove from the end of the array (last added groups)
    for (let i = currentCount - 1; i >= targetCount; i--) {
      canvas.remove(groups[i].object);
    }
  }

  // Reposition all remaining groups
  const updatedGroups = findGroupsByPath(canvas, basePath);

  updatedGroups.forEach((group, i) => {
    if (i < positions.length) {
      group.object.set({
        top: positions[i].top,
        left: positions[i].left,
      });
      group.object.setCoords();
    }
  });

  canvas.requestRenderAll();
};

/**
 * Get the current count of groups at a specific path
 *
 * @param {Object} canvas - Fabric canvas instance
 * @param {string} basePath - Base path of the group
 * @param {Array} thumbnailDataObjects - Optional thumbnail data objects
 * @returns {number} Count of groups
 */
export const getGroupCount = (canvas, basePath, thumbnailDataObjects = null) => {
  if (thumbnailDataObjects) {
    // When working with template data (not canvas)
    const match = basePath.match(/objects\[(\d+)\]/);
    if (!match) return 1;

    const baseIndex = parseInt(match[1], 10);
    const baseObject = thumbnailDataObjects[baseIndex];

    if (!baseObject || baseObject.type !== "group") return 1;

    const targetKey = baseObject.templatePayload?.key;
    if (!targetKey) return 1;

    const count = thumbnailDataObjects.filter(
      (obj) => obj.type === "group" && obj.templatePayload?.key === targetKey,
    ).length;

    return count || 1;
  }

  // When working with canvas
  const groups = findGroupsByPath(canvas, basePath);
  return groups.length || 1;
};
